import { ACTION_TYPE, ELEMENT_TYPE } from '@/constants';
import type {
  ActionData,
  BookData,
  BookPageData,
  ElementData,
  ImageLoadFailureKind
} from '@/types';
import { ImageElement } from '../../element/ImageElement.class';
import { Element } from '../../element/Element.class';
import { TextElement } from '../../element/TextElement.class';
import { MediaElement } from '../../element/MediaElement.class';
import { SoundElement } from '../../element/SoundElement.class';
import { EventElement } from '../../element/EventElement.class';
import { Book } from '../../book/Book.class';
import { BookPage } from '../../book/BookPage.class';
import { Action } from '@/modules/editor/timeline/action/Action.class';
import { PlayAction } from '@/modules/editor/timeline/action/PlayAction.class';

interface CreateElementOptions {
  preferHtml5Audio?: boolean;
  signal?: AbortSignal;
  imageTimeoutMs?: number;
  onImageLoadFailure?: (kind: ImageLoadFailureKind) => void;
}

export async function createAction(data: ActionData, options?: CreateElementOptions) {
  const element = await createElement(data.element, options);

  if (!element) return null;

  const action = createElementAction(data, element);

  return action;
}

export function createElementAction(data: ActionData, target: Element) {
  let action: Action | null;
  if (
    data.type === ACTION_TYPE.BACKGROUND ||
    data.type === ACTION_TYPE.MEDIA ||
    data.type === ACTION_TYPE.SOUND
  ) {
    action = new PlayAction(data, target);
  } else {
    action = new Action(data, target);
  }

  if (target instanceof MediaElement) {
    action.duration = (target as MediaElement).mediaDuration;
  }

  return action;
}

export async function createBook(data: BookData) {
  const book = new Book(data);

  return book;
}

export function createBookPage(data: BookPageData) {
  const page = new BookPage(data);

  return page;
}

export async function createElement(data?: ElementData, options?: CreateElementOptions) {
  if (!data) return null;

  let element: Element;

  switch (data.type) {
    case ELEMENT_TYPE.IMAGE:
      element = new ImageElement();
      break;
    case ELEMENT_TYPE.TEXT:
      element = new TextElement();
      break;
    case ELEMENT_TYPE.SOUND:
      element = new SoundElement({ preferHtml5: options?.preferHtml5Audio });
      break;
    case ELEMENT_TYPE.EVENT:
      element = new EventElement();
      break;
      // case ELEMENT_TYPE.GIF:
      //   element = new GifElement();
      //   break;
      // case ELEMENT_TYPE.MAIN_VIDEO:
      // case ELEMENT_TYPE.VIDEO:
      //   element = new VideoElement();
      //   break;
      // case ELEMENT_TYPE.BACKGROUND:
      //   element = new BackgroundElement();
      //   break;
      // case ELEMENT_TYPE.CAPTION:
      //   element = new CaptionElement();
      break;
    default:
      element = new Element();
      break;
  }

  element.data = data;
  const flag = await element.load({
    signal: options?.signal,
    timeoutMs: options?.imageTimeoutMs,
    onImageLoadFailure: options?.onImageLoadFailure
  });
  if (options?.signal?.aborted) {
    element.destroy();
    return null;
  }

  return flag ? element : null;
}
